import { getAuthToken } from '../../services/auth/tokenStorage';
import { sessionManager } from '../../services/sessionManager';
import { hashValidationAnswer } from '../../services/auth/validationVerifier';
import { getWrappingPublicKey } from '../../services/encryption/MasterKeyService';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Alert,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { useUserSelection } from '../../contexts/UserSelectionContext';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AcceptInvitation');

type RouteParams = {
  token: string;
};

type RootStackParamList = {
  Dashboard: undefined;
  Login: undefined;
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface InvitationDetails {
  inviterName: string;
  inviterUserId?: string;
  inviterAvatar?: string;
  role: 'successor' | 'keyholder';
  scopes: Record<string, any>;
  inviteeEmail?: string;
  inviteeFirstName?: string;
  inviteeLastName?: string;
  inviteePhone?: string;
  validationQuestion: string;
  // §10.2.1 — when non-null, the inviter's client pre-hashed the answer
  // and the backend expects the invitee to POST a PBKDF2 verifier rather
  // than plaintext. Null for legacy invitations.
  validationAnswerSalt?: string | null;
}

type ScreenState = 'loading' | 'details' | 'input' | 'processing' | 'success' | 'error' | 'locked';

// BUG-A 5.2.3 — after MAX_ATTEMPTS=5 wrong answers, the backend marks
// the invitation EXPIRED and throws UnauthorizedException with a
// structured payload. Nest's HTTP serializer can place that payload in
// several spots depending on version/middleware, so we look in all of
// them. The string fallback covers any older client/server combo where
// only the human-readable message survives.
const LOCKED_CODE = 'INVITATION_LOCKED';
const LOCKED_FALLBACK_PHRASE = 'invitation has been locked';
const LOCKED_DEFAULT_MESSAGE =
  'Too many failed attempts. This invitation has been locked. Ask the inviter to send a new one.';

function isLockoutResponse(result: unknown): boolean {
  if (!result || typeof result !== 'object') return false;
  const r = result as Record<string, unknown>;
  if (r.code === LOCKED_CODE) return true;
  if (
    r.message &&
    typeof r.message === 'object' &&
    (r.message as Record<string, unknown>).code === LOCKED_CODE
  ) {
    return true;
  }
  const messageStr =
    typeof r.message === 'string'
      ? r.message
      : typeof r.error === 'string'
      ? r.error
      : '';
  return messageStr.toLowerCase().includes(LOCKED_FALLBACK_PHRASE);
}

function extractLockoutMessage(result: unknown): string {
  if (!result || typeof result !== 'object') return LOCKED_DEFAULT_MESSAGE;
  const r = result as Record<string, unknown>;
  if (
    r.message &&
    typeof r.message === 'object' &&
    typeof (r.message as Record<string, unknown>).message === 'string'
  ) {
    return (r.message as Record<string, string>).message;
  }
  if (typeof r.message === 'string' && r.message.length > 0) return r.message;
  return LOCKED_DEFAULT_MESSAGE;
}

export const AcceptInvitationScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute();
  const { token } = route.params as RouteParams;

  const { refreshPrimaryUsers } = useUserSelection();
  const [state, setState] = useState<ScreenState>('loading');
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);
  const [validationAnswer, setValidationAnswer] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // 'load' = the screen could not reach the server or crashed while opening
  // the invitation (retry makes sense); 'invalid' = the server said no.
  const [errorKind, setErrorKind] = useState<'invalid' | 'load'>('invalid');

  useEffect(() => {
    checkAuthAndVerifyToken();
  }, []);

  // Refresh if the token is stale; true only when a fresh token is in hand.
  const hasUsableSession = async (): Promise<boolean> => {
    const userToken = await getAuthToken();
    if (!userToken) return false;
    if (await sessionManager.isTokenExpiredOrExpiring()) {
      await sessionManager.tryRefreshToken();
      if (await sessionManager.isTokenExpiredOrExpiring()) return false;
    }
    return true;
  };

  // Keep the invitation for after sign-up or sign-in, then offer both.
  const offerSignInOrCreate = async (details: InvitationDetails) => {
    await AsyncStorage.setItem('@invitation_token', token);
    setState('details');
    Alert.alert(
      'Sign In or Create Account',
      `${details.inviterName} has invited you as a ${details.role}. Sign in to your FinalKey account, or create one, to accept. The invitation will be waiting right after.`,
      [
        {
          text: 'Create Account',
          onPress: async () => {
            // Navigate to SignUp with pre-filled invitation data
            navigation.reset({
              index: 0,
              routes: [{
                name: 'Auth',
                state: {
                  routes: [{
                    name: 'SignUp',
                    params: {
                      isInvitedUser: true,
                      invitationToken: token,
                      email: details.inviteeEmail,
                      firstName: details.inviteeFirstName,
                      lastName: details.inviteeLastName,
                      phoneNumber: details.inviteePhone,
                    }
                  }]
                }
              }],
            });
          }
        },
        {
          text: 'Sign In',
          onPress: async () => {
            // Every way a sign-in can finish returns here through
            // navigation/pendingInvitation (the token was stored above).
            navigation.reset({
              index: 0,
              routes: [{
                name: 'Auth',
                state: {
                  routes: [{ name: 'Login' }]
                }
              }],
            });
          },
        },
        // Lets the visitor read the invitation first; Accept offers the
        // same choice again.
        { text: 'Not Now', style: 'cancel' },
      ]
    );
  };

  // One retry: a request sent while iOS is still bringing the app to the
  // foreground from the email link can fail outright ("Network request
  // failed"), which used to show "Invitation Not Found" for a good link.
  const fetchInvitation = async (): Promise<Response> => {
    const call = () =>
      fetch(`${API_URL}/api/invitations/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
    try {
      return await call();
    } catch (firstError) {
      logger.warn('Invitation check failed once, retrying:', firstError);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return call();
    }
  };

  const checkAuthAndVerifyToken = async () => {
    try {
      // First, verify the invitation token to get details
      const response = await fetchInvitation();

      const result = await response.json();

      if (!response.ok || !result.success) {
        // The stored token points to an invitation that no longer exists
        // (deleted, expired, or already consumed). Clear it so the auth
        // screens don't redirect the user back here on every subsequent
        // login. Without this, a primary/inviter whose device has a stale
        // token gets stuck seeing "Invitation Not Found" after each login.
        await AsyncStorage.removeItem('@invitation_token');
        setErrorKind('invalid');
        setState('error');
        setErrorMessage('This invitation is invalid or has expired.');
        return;
      }

      const details = result.data as InvitationDetails;
      setInvitationDetails(details);

      // A session is only usable if its token is fresh; a stale one left on
      // the phone (force-quit, days later) used to let the answer box show
      // and then fail at submit. Someone with no account gets the same
      // Create Account / Sign In choice at every one of those moments.
      if (!(await hasUsableSession())) {
        await offerSignInOrCreate(details);
        return;
      }

      // User is authenticated, show invitation details
      setIsAuthenticated(true);
      setState('details');
    } catch (error) {
      logger.error('Auth check error:', error);
      setErrorKind('load');
      setState('error');
      setErrorMessage('We couldn\'t load this invitation. Check your connection and try again.');
    }
  };

  const verifyInvitationToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setInvitationDetails(result.data);
        setState('details');
      } else {
        setState('error');
        setErrorMessage('We couldn\'t verify that invitation.');
      }
    } catch (error) {
      logger.error('Token verification error:', error);
      setState('error');
      setErrorMessage('We couldn\'t verify that invitation.');
    }
  };

  const handleAcceptInvitation = async () => {
    if (!validationAnswer.trim()) {
      Alert.alert('Missing Answer', 'Please enter the validation answer.');
      return;
    }

    setState('processing');

    try {
      // This call bypasses the app's HTTP client and its automatic refresh,
      // so make sure the token is fresh first. An expired one used to come
      // back from the server as a bare "Forbidden resource".
      if (!(await hasUsableSession()) || !invitationDetails) {
        if (invitationDetails) await offerSignInOrCreate(invitationDetails);
        else setState('error');
        return;
      }
      const userToken = await getAuthToken();

      // §10.2.1 — if the invitation was created under the new client-
      // hashing flow, the backend returns the per-invitation salt and
      // expects a PBKDF2 verifier. Fall back to sending plaintext only
      // for legacy invitations that predate this change (salt is null).
      const saltFromBackend = invitationDetails?.validationAnswerSalt;
      const body: Record<string, string> = { token };
      if (saltFromBackend) {
        body.validationAnswerVerifier = await hashValidationAnswer(
          validationAnswer,
          saltFromBackend,
        );
      } else {
        body.validationAnswer = validationAnswer.trim();
      }

      // Trusted-party wrapping (medium-term #2). If the invitee has already
      // enrolled a recovery code, we ship their published wrapping public
      // key alongside the acceptance so the primary can complete the
      // async handshake whenever they next open the app. An invitee who
      // hasn't enrolled is fine — they just land in the "handshake
      // pending" state until they do, at which point the primary's next
      // login picks them up via the pending-handshakes path.
      try {
        const wrappingPublicKey = await getWrappingPublicKey();
        if (wrappingPublicKey) {
          body.wrappingPublicKey = wrappingPublicKey;
        }
      } catch (err) {
        // Don't block acceptance on a wrapping-key lookup failure —
        // invitation acceptance should still succeed, the handshake will
        // just stay queued until the invitee enrolls.
        logger.warn('Unable to attach wrapping public key:', err);
      }

      const response = await fetch(`${API_URL}/api/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Clear the stored invitation token
        await AsyncStorage.removeItem('@invitation_token');
        setState('success');
      } else if (response.status === 401) {
        // Session expired mid-flow. Keep the invitation and offer the way
        // back in — including sign-up, for someone who has no account yet.
        setValidationAnswer('');
        await offerSignInOrCreate(invitationDetails);
      } else if (isLockoutResponse(result)) {
        // BUG-A 5.2.3 — invitation has been locked after MAX_ATTEMPTS
        // wrong answers. The invitation row is already EXPIRED on the
        // server, so further submissions will never succeed. Clear the
        // stored token and present a terminal screen — no retry loop.
        await AsyncStorage.removeItem('@invitation_token');
        setErrorMessage(extractLockoutMessage(result));
        setValidationAnswer('');
        setState('locked');
      } else {
        setState('input');
        setErrorMessage(
          (typeof result?.message === 'string' && result.message) ||
            'We couldn\'t verify that invitation.',
        );
        setValidationAnswer(''); // Clear the answer on failure
      }
    } catch (error) {
      logger.error('Accept invitation error:', error);
      setState('input');
      setErrorMessage('We couldn\'t verify that invitation.');
      setValidationAnswer('');
    }
  };

  const handleContinue = async () => {
    // The new connection is live server-side; pick it up before the
    // welcome screen offers the primary's dashboard.
    try {
      await refreshPrimaryUsers();
    } catch (error) {
      logger.debug('Primary refresh after acceptance failed (non-fatal):', error);
    }
    navigation.replace('TrustedPartyWelcome', {
      primaryName: invitationDetails?.inviterName ?? '',
      role: invitationDetails?.role ?? 'successor',
      primaryUserId: invitationDetails?.inviterUserId,
    });
  };

  const renderRoleDescription = (role: string) => {
    switch (role) {
      case 'successor':
        return 'You will have access to critical information and can make decisions on behalf of this user.';
      case 'keyholder':
        return "You'll help with everyday matters: contacts, final wishes and personal instructions. Documents and saved logins stay private.";
      default:
        return 'You will have special access permissions.';
    }
  };

  const renderScopesList = (scopes: Record<string, any>) => {
    const scopeKeys = Object.keys(scopes);
    if (scopeKeys.length === 0) return null;

    return (
      <View style={styles.scopesContainer}>
        <Text style={styles.scopesTitle}>You'll have access to:</Text>
        {scopeKeys.slice(0, 5).map((scope, index) => (
          <Text key={index} style={styles.scopeItem}>
            • {scope.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Text>
        ))}
        {scopeKeys.length > 5 && (
          <Text style={styles.scopeItem}>• And {scopeKeys.length - 5} more...</Text>
        )}
      </View>
    );
  };

  if (state === 'loading') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Verifying invitation...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (state === 'error') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerContainer}>
          <Image
            source={require('../../assets/icons/warning.png')}
            style={styles.errorIcon}
          />
          <Text style={styles.errorTitle}>
            {errorKind === 'load' ? 'Couldn\'t Load the Invitation' : 'Invitation Not Found'}
          </Text>
          <Text style={styles.errorDescription}>
            {errorMessage || 'This invitation may have expired or is no longer valid.'}
          </Text>
          {errorKind === 'load' && (
            <TouchableOpacity
              style={styles.terminalButton}
              onPress={() => {
                setState('loading');
                checkAuthAndVerifyToken();
              }}
            >
              <Text style={styles.primaryButtonText}>Try Again</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.terminalButton}
            onPress={() => navigation.replace('Dashboard')}
          >
            <Text style={styles.primaryButtonText}>Continue to App</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // BUG-A 5.2.3 — terminal lockout screen. Backend has marked the
  // invitation EXPIRED after MAX_ATTEMPTS=5 wrong answers; the only
  // path forward is for the inviter to send a fresh invitation. We
  // deliberately do NOT show a retry button here.
  if (state === 'locked') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerContainer}>
          <Image
            source={require('../../assets/icons/warning.png')}
            style={styles.errorIcon}
          />
          <Text style={styles.errorTitle}>Invitation Locked</Text>
          <Text style={styles.errorDescription}>
            {errorMessage || LOCKED_DEFAULT_MESSAGE}
          </Text>
          <TouchableOpacity
            style={styles.terminalButton}
            onPress={() => navigation.replace('Dashboard')}
          >
            <Text style={styles.primaryButtonText}>Continue to App</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (state === 'success') {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centerContainer}>
          <Image
            source={require('../../assets/icons/check.png')}
            style={styles.successIcon}
          />
          <Text style={styles.successTitle}>You're Connected!</Text>
          <Text style={styles.successDescription}>
            You're now connected to {invitationDetails?.inviterName}. You can access their shared information from your dashboard.
          </Text>
          <TouchableOpacity
            style={styles.successButton}
            onPress={handleContinue}
          >
            <Text style={styles.primaryButtonText}>Continue</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {state === 'details' && invitationDetails && (
          <>
            <View style={styles.header}>
              <Image
                source={require('../../assets/logo.png')}
                style={styles.logo}
                resizeMode="contain"
              />
              <Text style={styles.title}>You've Been Invited</Text>
            </View>

            <View style={styles.invitationCard}>
              <View style={styles.inviterInfo}>
                <View style={styles.avatarContainer}>
                  <Text style={styles.avatarText}>
                    {invitationDetails.inviterName.split(' ').map(n => n[0]).join('')}
                  </Text>
                </View>
                <View style={styles.inviterDetails}>
                  <Text style={styles.inviterName}>{invitationDetails.inviterName}</Text>
                  <Text style={styles.roleText}>
                    has invited you as a <Text style={styles.roleBold}>{invitationDetails.role}</Text>
                  </Text>
                </View>
              </View>

              <Text style={styles.roleDescription}>
                {renderRoleDescription(invitationDetails.role)}
              </Text>

              {renderScopesList(invitationDetails.scopes)}
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => {
                  // Signed out (the prompt was dismissed): offer it again.
                  if (!isAuthenticated && invitationDetails) offerSignInOrCreate(invitationDetails);
                  else setState('input');
                }}
              >
                <Text style={styles.primaryButtonText}>Accept Invitation</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.declineButton}
                onPress={() => {
                  Alert.alert(
                    'Decline Invitation',
                    'Are you sure you want to decline this invitation? You can continue using the app as a primary user.',
                    [
                      { text: 'Cancel', style: 'cancel' },
                      {
                        text: 'Decline',
                        style: 'destructive',
                        onPress: async () => {
                          // Clear the stored invitation token if any
                          await AsyncStorage.removeItem('@invitation_token');
                          // Navigate to dashboard/profile
                          navigation.replace('Dashboard');
                        }
                      }
                    ]
                  );
                }}
              >
                <Text style={styles.declineButtonText}>Decline</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {state === 'input' && invitationDetails && (
          <>
            <View style={styles.header}>
              <Image
                source={require('../../assets/logo.png')}
                style={styles.logo}
                resizeMode="contain"
              />
              <Text style={styles.title}>Verify Your Identity</Text>
            </View>

            <View style={styles.inputCard}>
              <Text style={styles.verificationText}>
                To complete the connection with {invitationDetails.inviterName}, please answer the validation question they provided.
              </Text>

              <View style={styles.questionContainer}>
                <Text style={styles.questionLabel}>Security Question from {invitationDetails.inviterName}:</Text>
                <Text style={styles.questionText}>"{invitationDetails.validationQuestion}"</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Your Answer</Text>
                <TextInput
                  style={styles.input}
                  value={validationAnswer}
                  onChangeText={setValidationAnswer}
                  placeholder="Enter your answer"
                  placeholderTextColor="#A9A9A9"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {errorMessage ? (
                  <Text style={styles.errorText}>{errorMessage}</Text>
                ) : null}
              </View>

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={() => setState('details')}
                >
                  <Text style={styles.secondaryButtonText}>Back</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={handleAcceptInvitation}
                >
                  <Text style={styles.primaryButtonText}>Verify & Accept</Text>
                </TouchableOpacity>
              </View>
            </View>
          </>
        )}

        {state === 'processing' && (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Processing invitation...</Text>
          </View>
        )}
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: ms(20),
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(20),
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(30),
  },
  logo: {
    width: s(80),
    height: s(80),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
  },
  invitationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(20),
    marginBottom: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  inviterInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(16),
  },
  avatarContainer: {
    width: s(50),
    height: s(50),
    borderRadius: ms(25),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(12),
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
  },
  inviterDetails: {
    flex: 1,
  },
  inviterName: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  roleText: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
  },
  roleBold: {
    fontWeight: 'bold',
    color: '#29BFC0',
  },
  roleDescription: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(16),
  },
  scopesContainer: {
    marginTop: ms(8),
  },
  scopesTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  scopeItem: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(4),
  },
  inputCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(20),
    marginBottom: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  verificationText: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(20),
    textAlign: 'center',
  },
  questionContainer: {
    backgroundColor: '#F0FDFF',
    borderRadius: ms(12),
    padding: ms(16),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  questionLabel: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  questionText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    fontStyle: 'italic',
    lineHeight: ms(22, 0.3),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(10),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: ms(12),
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  successButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(15),
    paddingHorizontal: ms(40),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  // Standalone CTA used on the terminal error / locked screens. Unlike
  // `primaryButton` (which carries flex:1 for the side-by-side button
  // rows in the form views), this has NO flex so it doesn't stretch to
  // fill the vertical space of `centerContainer` (a column flexbox).
  terminalButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(15),
    paddingHorizontal: ms(40),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(8),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  secondaryButtonText: {
    color: '#6B7280',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  declineButton: {
    flex: 1,
    backgroundColor: '#FF6B6B',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  declineButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginTop: ms(16),
    textAlign: 'center',
  },
  errorIcon: {
    width: s(64),
    height: s(64),
    tintColor: '#FF6B6B',
    marginBottom: ms(16),
  },
  errorTitle: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  errorDescription: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(24),
    lineHeight: ms(22, 0.3),
  },
  errorText: {
    color: '#FF6B6B',
    fontSize: ms(14, 0.3),
    marginTop: ms(5),
  },
  successIcon: {
    width: s(64),
    height: s(64),
    tintColor: '#10B981',
    marginBottom: ms(16),
  },
  successTitle: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
    textAlign: 'center',
  },
  successDescription: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(24),
    lineHeight: ms(22, 0.3),
  },
});

export default AcceptInvitationScreen;